import type { QueryClient } from '@tanstack/react-query';
import type { DataProvider } from 'react-admin';
import type { ReportsResource } from '../../../../Entities/Report';
import type { ActionLogsByUsersResource } from '../../../../Entities/ActionLogsByUser';
import { isNonEmptyString } from '../../../../utils';
import {
  REPORT_QUERY_STALE_TIME_MS,
  REPORT_TYPE_IDS,
} from '../../../../Components/ReportsWidget/utils';

export interface ActionLogsByUserFilter {
  greaterOrEqual?: {
    date?: string;
  };
  lessOrEqual?: {
    date?: string;
  };
  entity_types?: string[];
  action?: 'create' | 'update' | 'delete';
  attribute_names?: string[];
  user_identifier?: string;
}

interface ReportParams {
  from: string;
  to: string;
  entityTypes: string[];
  action?: 'create' | 'update' | 'delete';
  attributeNames: string[];
  userIdentifier?: string;
}

interface PrefetchedData {
  action_logs_by_users?: ActionLogsByUsersResource[];
}

function getReportParams(filter: ActionLogsByUserFilter): ReportParams {
  if (!isNonEmptyString(filter.greaterOrEqual?.date)) {
    throw new Error('Action log by user report requires `from` date');
  }

  if (!isNonEmptyString(filter.lessOrEqual?.date)) {
    throw new Error('Action log by user report requires `to` date');
  }

  return {
    from: filter.greaterOrEqual.date,
    to: filter.lessOrEqual.date,
    entityTypes: filter.entity_types ?? [],
    action: filter.action,
    attributeNames: filter.attribute_names ?? [],
    userIdentifier: isNonEmptyString(filter.user_identifier) ? filter.user_identifier : undefined,
  };
}

export function actionLogsByUserSavedReportQueryKey(
  filter: ActionLogsByUserFilter,
): readonly unknown[] {
  const params = getReportParams(filter);
  return [
    'action-logs-by-user-saved-report',
    params.from,
    params.to,
    params.entityTypes,
    params.action,
    params.attributeNames,
    params.userIdentifier,
  ] as const;
}

export async function createReportThenFetchActionLogsByUsers(
  dataProvider: DataProvider,
  filter: ActionLogsByUserFilter,
): Promise<{
  data: ActionLogsByUsersResource[];
  prefetched: PrefetchedData;
}> {
  const params = getReportParams(filter);
  const createResult = await dataProvider.create('reports', {
    data: {
      attributes: {
        report_type_id: REPORT_TYPE_IDS.actionLogsByUser,
        from: params.from,
        to: params.to,
        entity_types: params.entityTypes,
        action: params.action,
        attribute_names: params.attributeNames,
        user_identifier: params.userIdentifier,
      },
    } as Partial<ReportsResource<'action_logs_by_users'>>,
    meta: {
      prefetch: ['results'],
    },
  });

  const prefetchedFromCreate = (createResult.meta?.prefetched ?? {}) as PrefetchedData;

  return {
    data: (prefetchedFromCreate.action_logs_by_users ?? []) as ActionLogsByUsersResource[],
    prefetched: prefetchedFromCreate,
  };
}

export async function fetchOrCreateReportWithActionLogsByUsers(
  queryClient: QueryClient,
  dataProvider: DataProvider,
  filter: ActionLogsByUserFilter,
): Promise<{
  data: ActionLogsByUsersResource[];
  prefetched: PrefetchedData;
}> {
  return queryClient.fetchQuery({
    queryKey: actionLogsByUserSavedReportQueryKey(filter),
    queryFn: () => createReportThenFetchActionLogsByUsers(dataProvider, filter),
    staleTime: REPORT_QUERY_STALE_TIME_MS,
  });
}
